import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Audience } from '@epasu/core';
import { Archive, ArchiveRestore, ChevronDown, Pencil, Trash2, Upload } from 'lucide-react';
import { crm, type LeadListRow } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { PageHeader } from '@/components/PageHeader';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { cn } from '@/lib/utils';
import { RenameLeadListDialog } from './RenameLeadListDialog';

/**
 * Die Lead-Listen-Übersicht.
 *
 * Zeigt nur Name, Zielgruppe, Anzahl und Notiz je Liste — keine eigene
 * Mitgliedertabelle: `contacts.list` bekommt in dieser Stufe bewusst keinen
 * Listenfilter (siehe Brief zu Task 6). Die Detailzeile verlinkt stattdessen
 * auf den Reiter „Leads“ mit gesetztem Listenfilter — die Leads-Seite hat den Filter,
 * alle Spalten und die Auswahlaktionen schon; eine zweite Tabelle für
 * dieselben Zeilen wäre genau der zweite Ort, den dieses Vorhaben sonst
 * überall vermeidet.
 */
export function LeadListsPage({ onImport }: { onImport?: () => void } = {}) {
  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [listen, setListen] = useState<LeadListRow[] | null>(null);
  const [archivierteZeigen, setArchivierteZeigen] = useState(false);
  const [aufgeklappt, setAufgeklappt] = useState<string | null>(null);

  const [bearbeiten, setBearbeiten] = useState<LeadListRow | null>(null);
  const [loeschen, setLoeschen] = useState<LeadListRow | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    void crm.audiences.list().then(setAudiences);
  }, []);

  const laden = useCallback(() => {
    void crm.leadLists.list({ includeArchived: true }).then(setListen);
  }, []);
  useEffect(laden, [laden]);

  const audienceName = useMemo(
    () => new Map(audiences.map((a) => [a.id, a.name])),
    [audiences],
  );

  const sichtbar = useMemo(
    () => (listen ?? []).filter((l) => archivierteZeigen || !l.archived),
    [listen, archivierteZeigen],
  );

  const archivierenUmschalten = async (liste: LeadListRow) => {
    setFehler(null);
    try {
      await crm.leadLists.setArchived(liste.id, !liste.archived);
      laden();
    } catch (err) {
      setFehler(
        err instanceof Error ? err.message : 'Archivieren fehlgeschlagen',
      );
    }
  };

  return (
    <div>
      <PageHeader>
        <div className="flex flex-wrap items-center gap-2">
          {onImport && (
            <Button variant="outline" onClick={onImport}>
              <Upload className="mr-1.5 h-4 w-4" />
              CSV importieren
            </Button>
          )}
          <label className="ml-auto flex items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={archivierteZeigen}
              onChange={(e) => setArchivierteZeigen(e.target.checked)}
            />
            Archivierte einblenden
          </label>
        </div>
      </PageHeader>

      {fehler && (
        <Card className="mb-4 border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          {fehler}
        </Card>
      )}

      <Card className="overflow-hidden">
        {listen === null ? (
          <p className="p-6 text-sm text-muted-foreground">Lade …</p>
        ) : sichtbar.length === 0 ? (
          <p className="p-8 text-center text-sm text-muted-foreground">
            {archivierteZeigen
              ? 'Noch keine Lead-Liste.'
              : 'Noch keine aktive Lead-Liste. Auf „Leads“ lässt sich eine Auswahl als Liste speichern.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="w-8 px-4 py-3" />
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Zielgruppe</th>
                  <th className="px-4 py-3 font-medium">Anzahl</th>
                  <th className="px-4 py-3 font-medium">Notiz</th>
                  <th className="w-28 px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {sichtbar.map((l) => {
                  const offen = aufgeklappt === l.id;
                  return (
                    <Fragment key={l.id}>
                      <tr
                        className="cursor-pointer border-b last:border-0 hover:bg-muted/40"
                        onClick={() => setAufgeklappt(offen ? null : l.id)}
                      >
                        <td className="px-4 py-3">
                          <ChevronDown
                            className={cn(
                              'h-4 w-4 text-muted-foreground transition-transform',
                              offen && 'rotate-180',
                            )}
                          />
                        </td>
                        <td className="px-4 py-3 font-medium">
                          {l.name}
                          {l.archived && (
                            <span className="ml-2 rounded bg-secondary px-1.5 py-0.5 text-[11px] font-normal text-secondary-foreground">
                              archiviert
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {audienceName.get(l.audienceId) ?? '—'}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{l.memberCount}</td>
                        <td className="max-w-xs px-4 py-3 text-muted-foreground">
                          <span className="block truncate">{l.notes || '—'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div
                            className="flex justify-end gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`${l.name} bearbeiten`}
                              onClick={() => setBearbeiten(l)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={l.archived ? `${l.name} reaktivieren` : `${l.name} archivieren`}
                              title={l.archived ? 'Reaktivieren' : 'Archivieren'}
                              onClick={() => void archivierenUmschalten(l)}
                            >
                              {l.archived ? (
                                <ArchiveRestore className="h-4 w-4" />
                              ) : (
                                <Archive className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`${l.name} löschen`}
                              onClick={() => setLoeschen(l)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {offen && (
                        <tr className="border-b bg-muted/20 last:border-0">
                          <td />
                          <td colSpan={5} className="px-4 py-3 text-sm text-muted-foreground">
                            <p>
                              Zielgruppe {audienceName.get(l.audienceId) ?? '—'}.{' '}
                              {l.memberCount > 0 ? (
                                <Link
                                  to={`/akquise?reiter=leads&liste=${l.id}`}
                                  className="font-medium text-brand hover:underline"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {l.memberCount} {l.memberCount === 1 ? 'Lead' : 'Leads'} anzeigen
                                </Link>
                              ) : (
                                'Noch keine Leads in dieser Liste.'
                              )}
                            </p>
                            {l.notes && <p className="mt-1 whitespace-pre-wrap">{l.notes}</p>}
                            <p className="mt-1 text-xs">
                              Weitere Mitglieder kommen über „Auswahl als Liste speichern“ auf
                              der Leads-Seite dazu.
                            </p>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {bearbeiten && (
        <RenameLeadListDialog
          liste={bearbeiten}
          onClose={() => setBearbeiten(null)}
          onSaved={() => {
            setBearbeiten(null);
            laden();
          }}
        />
      )}

      {loeschen && (
        <DeleteLeadListDialog
          liste={loeschen}
          onClose={() => setLoeschen(null)}
          onDeleted={() => {
            setLoeschen(null);
            laden();
          }}
        />
      )}
    </div>
  );
}

/**
 * Löschen mit Rückfrage — die Liste und ihre Leads.
 *
 * Vorher wird gezählt, was mitgeht und was bleibt: ein Löschen, das Leads
 * mitnimmt, darf nicht erst hinterher sagen, wie viele es waren.
 *
 * Führt eine Kampagne die Liste noch als Herkunft, ist das keine
 * Fehlermeldung, sondern die richtige Antwort auf die Frage — deshalb wird
 * der bekannte Grund abgefangen und neutral gezeigt (`bg-secondary`), statt
 * ihn zu werfen und im roten Fehlerbanner von `ConfirmDialog` landen zu
 * lassen. Derselbe Kniff wie bei `DeleteOrganizationDialog` in
 * `OrganizationsPage.tsx` und `DemoteCustomerDialog`.
 */
function DeleteLeadListDialog({
  liste,
  onClose,
  onDeleted,
}: {
  liste: LeadListRow;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [impact, setImpact] = useState<Awaited<
    ReturnType<typeof crm.leadLists.deletionImpact>
  > | null>(null);
  const [blockiert, setBlockiert] = useState(false);

  useEffect(() => {
    void crm.leadLists.deletionImpact(liste.id).then(setImpact);
  }, [liste.id]);

  const gesperrt = blockiert || !!impact?.inUse;
  const leads = (n: number) => `${n} ${n === 1 ? 'Lead' : 'Leads'}`;

  return (
    <ConfirmDialog
      title={`${liste.name} löschen?`}
      confirmLabel={
        impact && !gesperrt && impact.leads > 0
          ? `Liste und ${leads(impact.leads)} löschen`
          : 'Löschen'
      }
      destructive
      onCancel={onClose}
      onConfirm={async () => {
        const ergebnis = await crm.leadLists.delete(liste.id);
        if (ergebnis.deleted) {
          onDeleted();
        } else {
          setBlockiert(true);
        }
      }}
    >
      {gesperrt ? (
        <p className="rounded-md bg-secondary p-3 text-sm text-muted-foreground">
          Diese Liste wird von einer Kampagne als Herkunft geführt und lässt
          sich deshalb nicht löschen.
        </p>
      ) : impact === null ? (
        <p className="text-sm text-muted-foreground">Prüfe, was daran hängt …</p>
      ) : (
        <div className="space-y-2 text-sm text-muted-foreground">
          <p>
            {impact.leads > 0 ? (
              <>
                Mit der Liste werden{' '}
                <strong className="text-foreground">{leads(impact.leads)}</strong> gelöscht.
              </>
            ) : (
              'Es werden keine Leads mitgelöscht — nur die Liste verschwindet.'
            )}
          </p>
          {impact.keptInOtherLists + impact.keptContacted + impact.keptContacts > 0 && (
            <ul className="list-disc space-y-1 pl-5">
              {impact.keptInOtherLists > 0 && (
                <li>
                  {leads(impact.keptInOtherLists)} bleiben, weil sie noch in einer anderen
                  Liste stehen.
                </li>
              )}
              {impact.keptContacted > 0 && (
                <li>
                  {leads(impact.keptContacted)} bleiben, weil sie angeschrieben wurden oder
                  geschrieben haben.
                </li>
              )}
              {impact.keptContacts > 0 && (
                <li>
                  {impact.keptContacts}{' '}
                  {impact.keptContacts === 1 ? 'Eintrag ist' : 'Einträge sind'} inzwischen
                  Kontakt und {impact.keptContacts === 1 ? 'bleibt' : 'bleiben'}.
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </ConfirmDialog>
  );
}
